import type { ErrorResponse } from '@padhai/shared';

/**
 * Thin fetch wrapper. Every API error arrives in one shape, so callers render a
 * message instead of guessing.
 *
 * `credentials: 'include'` on every call: the session lives in an httpOnly
 * cookie, which the app can neither read nor accidentally log.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Empty in dev, where the Vite proxy keeps the browser same-origin. */
const BASE_URL = import.meta.env.VITE_API_URL ?? '';

async function request<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        accept: 'application/json',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    const parsed = (await response.json().catch(() => null)) as ErrorResponse | null;
    throw new ApiError(
      response.status,
      parsed?.error.code ?? 'request_failed',
      parsed?.error.message ?? 'Something went wrong. Please try again.',
    );
  }

  return (await response.json()) as T;
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>('GET', path, undefined, signal);
}

export function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>('POST', path, body, signal);
}

export function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>('PUT', path, body, signal);
}
